import { Reservation, Table, CombinedTable, TableAvailability, TableWeekdayAvailability } from "@/types/table";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useFloorPlanDrag } from "@/contexts/FloorPlanDragContext";
import { QuickTableChangeDialog } from "../QuickTableChangeDialog";
import {
  Users, UtensilsCrossed, MessageSquare, GripVertical, Baby,
  Check, X, CalendarClock, ArrowRightLeft, StickyNote,
} from "lucide-react";
import { StrollerIcon } from "@/components/icons/StrollerIcon";

interface ReservationSettings {
  bookingMode: string;
  reservationDurationMinutes: number;
}

interface FloorPlanReservationPanelProps {
  timeSlotReservations: Reservation[];
  selectedTimeSlot: string | null;
  timeSlots: { start: string; end: string }[];
  tables: Table[];
  combinedTables: CombinedTable[];
  reservations: Reservation[];
  availabilities: TableAvailability[];
  weekdayAvailabilities: TableWeekdayAvailability[];
  reservationSettings: ReservationSettings | null;
  onEditReservation?: (reservation: Reservation) => void;
  onStatusChange?: (reservation: Reservation, status: "arrivé" | "annulé" | "planifié") => void;
  onTableChange?: (reservationId: string, tableId: string | null, combinedTableId: string | null) => void;
  fullscreen?: boolean;
}

export const FloorPlanReservationPanel = ({
  timeSlotReservations,
  selectedTimeSlot,
  timeSlots,
  tables,
  combinedTables,
  reservations,
  availabilities,
  weekdayAvailabilities,
  reservationSettings,
  onEditReservation,
  onStatusChange,
  onTableChange,
  fullscreen,
}: FloorPlanReservationPanelProps) => {
  const { dragState, startDrag, endDrag } = useFloorPlanDrag();

  const getTableName = (reservation: Reservation): string => {
    if (reservation.tableId) {
      return tables.find(t => t.id === reservation.tableId)?.name || 'Table inconnue';
    }
    if (reservation.combinedTableId) {
      return combinedTables.find(ct => ct.id === reservation.combinedTableId)?.name || 'Table combinée';
    }
    return 'Non assigné';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'arrivé':
        return <Badge className="bg-green-500/20 text-green-700 border-green-500/30">Arrivé</Badge>;
      case 'annulé':
        return <Badge variant="outline" className="text-muted-foreground border-muted-foreground/30 bg-muted/50">Annulé</Badge>;
      default:
        return <Badge variant="secondary">Planifié</Badge>;
    }
  };

  const handleDragStart = (e: React.DragEvent, reservation: Reservation) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', reservation.id);
    const dragElement = e.currentTarget as HTMLElement;
    e.dataTransfer.setDragImage(dragElement, 20, 20);
    startDrag(reservation);
  };

  return (
    <Card className={cn(
      fullscreen
        ? "w-full h-full border-0 rounded-none flex flex-col"
        : "flex-1 min-w-0 md:min-w-[220px] md:max-w-[300px] lg:min-w-[280px] lg:max-w-[400px]"
    )}>
      <div className="p-4 border-b shrink-0">
        <h3 className="font-semibold flex items-center gap-2">
          <UtensilsCrossed className="w-4 h-4" />
          Réservations du créneau
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          {selectedTimeSlot ? `${selectedTimeSlot} - ${timeSlots.find(ts => ts.start === selectedTimeSlot)?.end || ''}` : 'Aucun créneau sélectionné'}
        </p>
      </div>
      <ScrollArea className={fullscreen ? "flex-1" : "h-[400px]"}>
        {timeSlotReservations.length === 0 ? (
          <div className="p-4 text-center text-muted-foreground">
            <UtensilsCrossed className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>Aucune réservation pour ce créneau</p>
          </div>
        ) : (
          <div className="p-1.5 space-y-1.5">
            {timeSlotReservations.map(reservation => (
              <div
                key={reservation.id}
                className={cn(
                  "p-2 rounded-lg border bg-card text-xs transition-all",
                  reservation.status === 'annulé' && "opacity-50 text-muted-foreground",
                  onTableChange && reservation.status !== 'annulé' && "cursor-grab active:cursor-grabbing",
                  dragState.isDragging && dragState.draggedReservation?.id === reservation.id && "opacity-50 scale-95 ring-2 ring-primary"
                )}
                draggable={!!onTableChange && reservation.status !== 'annulé'}
                onDragStart={(e) => {
                  e.stopPropagation();
                  handleDragStart(e, reservation);
                }}
                onDragEnd={(e) => {
                  e.stopPropagation();
                  endDrag();
                }}
              >
                <div 
                  className="hover:bg-accent/50 rounded -m-2 p-2 transition-colors"
                  onClick={() => {
                    if (!dragState.isDragging) {
                      onEditReservation?.(reservation);
                    }
                  }}
                >
                  {/* Header row with drag handle */}
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-1.5">
                      {onTableChange && reservation.status !== 'annulé' && (
                        <GripVertical className="w-3 h-3 text-muted-foreground/50 flex-shrink-0" />
                      )}
                      {/* Show exact time in TIME mode */}
                      {reservationSettings?.bookingMode === 'time' && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 font-mono">
                          {reservation.startTime.substring(0, 5)}
                        </Badge>
                      )}
                      <span className="font-medium truncate">
                        {reservation.customerFirstName} {reservation.customerLastName}
                      </span>
                      {reservation.source === 'walk_in' && (
                        <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 bg-accent/20 border-accent/40">Walk-in</Badge>
                      )}
                    </div>
                    {getStatusBadge(reservation.status)}
                  </div>
                  
                  {/* Info row - compact with quick table change */}
                  <div className="flex items-center gap-3 text-muted-foreground">
                    {onTableChange ? (
                      <QuickTableChangeDialog
                        reservation={{
                          id: parseInt(reservation.id) || 0,
                          tableId: reservation.tableId,
                          combinedTableId: reservation.combinedTableId,
                          persons: reservation.numberOfPeople,
                          time: reservation.startTime.substring(0, 5),
                          reservationDate: reservation.date instanceof Date ? reservation.date : new Date(reservation.date),
                        }}
                        tables={tables}
                        combinedTables={combinedTables}
                        allReservations={reservations}
                        availabilities={availabilities}
                        weekdayAvailabilities={weekdayAvailabilities}
                        onTableChange={(_, tableId, combinedTableId) => onTableChange(reservation.id, tableId, combinedTableId)}
                        disabled={reservation.status === 'annulé'}
                        reservationSettings={reservationSettings ? {
                          bookingMode: reservationSettings.bookingMode as 'slot' | 'time',
                          reservationDurationMinutes: reservationSettings.reservationDurationMinutes,
                        } : undefined}
                      >
                        <button 
                          className="flex items-center gap-1 hover:bg-muted rounded px-1 -mx-1 transition-colors group"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <UtensilsCrossed className="w-3 h-3" />
                          {getTableName(reservation)}
                          <ArrowRightLeft className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      </QuickTableChangeDialog>
                    ) : (
                      <span className="flex items-center gap-1">
                        <UtensilsCrossed className="w-3 h-3" />
                        {getTableName(reservation)}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {reservation.numberOfPeople}
                    </span>
                    {(reservation.numberOfChildren ?? 0) > 0 && (
                      <span className="flex items-center gap-0.5" title={`${reservation.numberOfChildren} enfant(s)`}>
                        <Baby className="w-3 h-3" />+{reservation.numberOfChildren}
                      </span>
                    )}
                    {reservation.hasStroller && (
                      <StrollerIcon size={12} className="text-muted-foreground" aria-label="Poussette" />
                    )}
                    {reservation.internalNotes && (
                      <StickyNote className="w-3 h-3 text-violet-500" aria-label="Note interne" />
                    )}
                  </div>
                  
                  {/* Notes - compact */}
                  {reservation.notes && (
                    <div className="mt-1.5 flex items-start gap-1 p-1.5 rounded bg-orange-500/10 text-orange-700 dark:text-orange-400">
                      <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      <span className="line-clamp-1">{reservation.notes}</span>
                    </div>
                  )}
                </div>
                
                {/* Status change buttons - compact */}
                <div className="flex items-center gap-1 pt-1.5 border-t mt-1.5">
                  <Button
                    variant={reservation.status === 'arrivé' ? 'default' : 'outline'}
                    size="sm"
                    className={cn(
                      "flex-1 h-6 text-[10px] px-1",
                      reservation.status === 'arrivé' && "bg-green-600 hover:bg-green-700"
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (reservation.status !== 'arrivé') {
                        onStatusChange?.(reservation, 'arrivé');
                      }
                    }}
                  >
                    <Check className="w-3 h-3 mr-0.5" />
                    Arrivé
                  </Button>
                  <Button
                    variant={reservation.status === 'planifié' ? 'default' : 'outline'}
                    size="sm"
                    className={cn(
                      "flex-1 h-6 text-[10px] px-1",
                      reservation.status === 'planifié' && "bg-blue-600 hover:bg-blue-700"
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (reservation.status !== 'planifié') {
                        onStatusChange?.(reservation, 'planifié');
                      }
                    }}
                  >
                    <CalendarClock className="w-3 h-3 mr-0.5" />
                    Planifié
                  </Button>
                  <Button
                    variant={reservation.status === 'annulé' ? 'destructive' : 'outline'}
                    size="sm"
                    className="flex-1 h-6 text-[10px] px-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (reservation.status !== 'annulé') {
                        onStatusChange?.(reservation, 'annulé');
                      }
                    }}
                  >
                    <X className="w-3 h-3 mr-0.5" />
                    Annulé
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </Card>
  );
};
